import { forwardRef, ReactNode } from 'react'
import * as RadixAlertDialog from '@radix-ui/react-alert-dialog'

import { Button, Divider } from '@foxino/components-web'
import { XClose } from '@foxino/icons-react'

import resolveTailwindClasses from '@app/utils/resolveTailwindClasses'

type Props = React.HTMLProps<HTMLDivElement> & {
  title: string
  description?: ReactNode
  actionButtons?: ReactNode
  withDivider?: boolean
  isOpen?: boolean
  showOverlay?: boolean
  onAbort?: () => void
}

/**
 * @group Components
 * @category Component
 */
export const AlertDialog = forwardRef(
  (
    {
      className,
      title,
      description,
      disabled,
      children,
      actionButtons,
      withDivider,
      isOpen,
      showOverlay = true,
      onAbort,
      ...restProps
    }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    return (
      <RadixAlertDialog.Root open={isOpen}>
        {children && (
          <RadixAlertDialog.Trigger asChild disabled={disabled} onAbort={onAbort}>
            <div>{children}</div>
          </RadixAlertDialog.Trigger>
        )}
        <RadixAlertDialog.Portal>
          {showOverlay && (
            <RadixAlertDialog.Overlay
              className="fixed inset-0 z-[1000] bg-black/75 backdrop-blur-sm"
              onClick={onAbort}
            />
          )}
          <RadixAlertDialog.Content
            {...restProps}
            ref={ref}
            className={resolveTailwindClasses(
              'fixed left-1/2 top-1/2 z-[1001] max-h-[85vh] w-[90vw] max-w-[700px] translate-x-[-50%] translate-y-[-50%] rounded-2xl border border-primary-800 bg-primary-1100 p-3 focus:outline-none md:w-auto',
              className
            )}
          >
            <div>
              <div className="flex justify-between pt-1">
                <RadixAlertDialog.Title className="m-0 text-xl font-bold" asChild>
                  <div className="flex w-full justify-between text-primary-100">
                    <div className="w-full pr-10 text-center">{title}</div>

                    <RadixAlertDialog.Action asChild>
                      <Button
                        className="absolute right-3 top-3 z-[1000] rounded-[10px] bg-primary-900"
                        color="secondary"
                        autoFocus
                        onClick={onAbort}
                        LeadingIcon={XClose}
                      />
                    </RadixAlertDialog.Action>
                  </div>
                </RadixAlertDialog.Title>
              </div>
              {withDivider && <Divider className="px-[-15px]" />}
              <RadixAlertDialog.Description className="mb-7 mt-4 text-center text-base text-primary-200">
                {description}
              </RadixAlertDialog.Description>
              <RadixAlertDialog.Action asChild className="m-auto w-full">
                {actionButtons}
              </RadixAlertDialog.Action>
            </div>
          </RadixAlertDialog.Content>
        </RadixAlertDialog.Portal>
      </RadixAlertDialog.Root>
    )
  }
)

AlertDialog.displayName = 'AlertDialog'
